import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import { API, axios } from './adminConfig';

const QuizzesTab = ({ quizzes, contentList, fetchQuizzes, fetchContentForQuizzes, handleDeleteQuiz }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [selectedContent, setSelectedContent] = useState('');
  const [quizTitle, setQuizTitle] = useState('');
  const [questions, setQuestions] = useState([
    { question: '', options: ['', '', '', ''], correct_answer_index: 0 }
  ]);
  const [creating, setCreating] = useState(false);

  const getContentTitle = (contentId) => {
    const content = contentList.find(c => c.id === contentId);
    return content ? content.title : 'Unknown Content';
  };

  const startEdit = (quiz) => {
    setEditingQuiz(quiz);
    setSelectedContent(quiz.content_id);
    setQuizTitle(quiz.title);
    setQuestions(quiz.questions);
    setShowCreateModal(true);
  };

  const cancelEdit = () => {
    setEditingQuiz(null);
    setSelectedContent('');
    setQuizTitle('');
    setQuestions([{ question: '', options: ['', '', '', ''], correct_answer_index: 0 }]);
    setShowCreateModal(false);
  };

  useEffect(() => {
    fetchQuizzes();
    fetchContentForQuizzes();
  }, []);

  const addQuestion = () => {
    setQuestions([...questions, { question: '', options: ['', '', '', ''], correct_answer_index: 0 }]);
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...questions];
    newQuestions[index][field] = value;
    setQuestions(newQuestions);
  };

  const updateOption = (qIndex, oIndex, value) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].options[oIndex] = value;
    setQuestions(newQuestions);
  };

  const handleSaveQuiz = async () => {
    if (!selectedContent || !quizTitle) {
      alert('Please select content and enter a quiz title');
      return;
    }

    const validQuestions = questions.filter(q => 
      q.question.trim() && q.options.filter(o => o.trim()).length >= 2
    );

    if (validQuestions.length === 0) {
      alert('Please add at least one valid question with options');
      return;
    }

    setCreating(true);
    try {
      if (editingQuiz) {
        await axios.delete(`${API}/admin/quizzes/${editingQuiz.id}`);
        await axios.post(`${API}/admin/quizzes`, {
          content_id: selectedContent,
          title: quizTitle,
          questions: validQuestions
        });
        alert('Quiz updated successfully!');
      } else {
        await axios.post(`${API}/admin/quizzes`, {
          content_id: selectedContent,
          title: quizTitle,
          questions: validQuestions
        });
        alert('Quiz created successfully!');
      }

      cancelEdit();
      fetchQuizzes();
    } catch (error) {
      console.error('Error saving quiz:', error);
      alert('Error saving quiz');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Create Quiz Button */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Content Quizzes</CardTitle>
              <CardDescription>Create quizzes for educational content</CardDescription>
            </div>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Quiz
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Create/Edit Quiz Modal */}
      {showCreateModal && (
        <Card className="border-2 border-burgundy">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>{editingQuiz ? 'Edit Quiz' : 'Create New Quiz'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={cancelEdit}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Select Content</label>
              <select
                value={selectedContent}
                onChange={(e) => setSelectedContent(e.target.value)}
                className="w-full p-2 border rounded-lg"
              >
                <option value="">Choose content...</option>
                {contentList.map((content) => (
                  <option key={content.id} value={content.id}>
                    {content.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Quiz Title</label>
              <Input
                value={quizTitle}
                onChange={(e) => setQuizTitle(e.target.value)}
                placeholder="e.g., Test Your Knowledge"
              />
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium">Questions</label>
                <Button variant="outline" size="sm" onClick={addQuestion}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Question
                </Button>
              </div>

              {questions.map((q, qIdx) => (
                <Card key={qIdx} className="p-4 border">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium">Question {qIdx + 1}</h4>
                      {questions.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeQuestion(qIdx)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <Input
                      value={q.question}
                      onChange={(e) => updateQuestion(qIdx, 'question', e.target.value)}
                      placeholder="Enter question..."
                    />

                    <div className="space-y-2">
                      <label className="text-sm text-gray-600">Options (mark correct answer):</label>
                      {q.options.map((opt, oIdx) => (
                        <div key={oIdx} className="flex gap-2 items-center">
                          <input
                            type="radio"
                            name={`correct-${qIdx}`}
                            checked={q.correct_answer_index === oIdx}
                            onChange={() => updateQuestion(qIdx, 'correct_answer_index', oIdx)}
                            className="w-4 h-4"
                          />
                          <Input
                            value={opt}
                            onChange={(e) => updateOption(qIdx, oIdx, e.target.value)}
                            placeholder={`Option ${String.fromCharCode(65 + oIdx)}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <Button
              onClick={handleSaveQuiz}
              disabled={creating}
              className="w-full"
            >
              {creating ? 'Saving...' : (editingQuiz ? 'Update Quiz' : 'Create Quiz')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Existing Quizzes */}
      <Card>
        <CardHeader>
          <CardTitle>Existing Quizzes</CardTitle>
        </CardHeader>
        <CardContent>
          {quizzes.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No quizzes created yet</p>
          ) : (
            <div className="space-y-4">
              {quizzes.map((quiz) => (
                <Card key={quiz.id} className="border">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{quiz.title}</CardTitle>
                        <CardDescription>
                          <span className="font-semibold text-burgundy">Content:</span> {getContentTitle(quiz.content_id)}
                        </CardDescription>
                        <CardDescription>
                          {quiz.questions.length} questions • Created {new Date(quiz.created_at).toLocaleDateString()}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEdit(quiz)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteQuiz(quiz.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {quiz.questions.map((q, idx) => (
                        <div key={idx} className="text-sm">
                          <p className="font-medium">{idx + 1}. {q.question}</p>
                          <ul className="ml-4 text-gray-600">
                            {q.options.map((opt, oIdx) => (
                              <li key={oIdx} className={oIdx === q.correct_answer_index ? 'text-green-600 font-medium' : ''}>
                                {String.fromCharCode(65 + oIdx)}. {opt} {oIdx === q.correct_answer_index && '✓'}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export { QuizzesTab };
